import { MouseEventHandler } from 'react';
import { ITEM_HEIGHT } from './atoms';
import styled, { css } from 'styled-components';
import { colors, r4, s8 } from '@ardoq/design-tokens';
import { SmallGhostButton } from '@ardoq/button';
import { CloseIcon } from '@ardoq/icons';
import { focusRingCSS } from '@ardoq/style-helpers';
import { FlexBox } from '@ardoq/layout';
import { text1 } from '@ardoq/typography';
import { MetamodelTypeIcon } from './MetamodelTypeIcon';
import { getMetamodelTypeColor } from './utils';
import { TextOverflow } from '@ardoq/popovers';
import { MetamodelType } from '../state/types';
import { useMetamodelEditorContext } from '../state';

const Name = styled(TextOverflow)`
  ${text1};
  /* ~60 characters */
  max-width: 420px;
  flex: 1;
  user-select: none;
`;

const LeafWrapper = styled(FlexBox).attrs({ gap: 'small' })<{
  $isEditable?: boolean;
  $isSelected?: boolean;
  onClick?: MouseEventHandler<HTMLDivElement>;
  tabIndex?: number;
}>`
  display: flex;
  border-radius: ${r4};
  background-color: ${colors.white950};
  border: 1px solid ${colors.grey300};
  height: ${ITEM_HEIGHT}px;
  min-width: 140px;
  align-items: center;
  padding-left: 12px;
  max-width: min-content;

  & > ${Name} {
    &:last-child {
      padding-right: ${s8};
    }
  }

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      &&& {
        background-color: ${colors.blue50};
      }
      ${focusRingCSS}
    `}

  /* for the left side color bar */
  position: relative;

  ${({ $isEditable }) =>
    $isEditable &&
    css`
      cursor: pointer;
      &:hover {
        background-color: ${colors.transparent10};
      }
    `}
`;

const LeafColorBar = styled.div`
  display: block;
  position: absolute;
  width: 5px;
  border-radius: ${r4} 0 0 ${r4};
  height: 100%;
  top: 0;
  left: 0;
`;

type LeafProps = {
  metamodelType: MetamodelType;
  isEditable?: boolean;
  isRemovable?: boolean;
};

export const Leaf = ({ metamodelType, isEditable, isRemovable }: LeafProps) => {
  const {
    editorError,
    selectedMetamodelTypeId,
    setSelectedMetamodelTypeId,
    deleteMetamodelType,
  } = useMetamodelEditorContext();

  const onDeleteModelType = (e: any) => {
    e.stopPropagation();
    deleteMetamodelType({ id: metamodelType.id });
  };

  return (
    <LeafWrapper
      $isEditable={isEditable}
      $isSelected={selectedMetamodelTypeId === metamodelType.id}
      onClick={() =>
        !editorError?.message &&
        setSelectedMetamodelTypeId({ id: metamodelType.id })
      }
      tabIndex={0}
    >
      <LeafColorBar
        style={{ backgroundColor: getMetamodelTypeColor(metamodelType) }}
      />
      <MetamodelTypeIcon metamodelType={metamodelType} />
      <Name>{metamodelType.name}</Name>
      {isEditable && isRemovable && (
        <SmallGhostButton
          onClick={onDeleteModelType}
          dataTestId="delete-model-type"
        >
          <CloseIcon />
        </SmallGhostButton>
      )}
    </LeafWrapper>
  );
};
